import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Phone, MessageCircle, CheckCircle, Clock, IndianRupee, FileText, Beaker, AlertCircle, ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';
import { publicAPI } from '../services/api';
import { companyInfo } from '../data/mock';
import { Button } from '../components/ui/button';

const PackageDetailPage = () => {
  const { slug } = useParams();
  const [packageData, setPackageData] = useState(null);
  const [allPackages, setAllPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Try to get package by slug first
        try {
          const response = await publicAPI.getPackageBySlug(slug);
          setPackageData(response.data);
        } catch (err) {
          // If not found by slug, get all packages and find by slug or name match
          const allRes = await publicAPI.getPackages();
          setAllPackages(allRes.data);
          const found = allRes.data.find(p => 
            p.slug === slug || 
            p.name.toLowerCase().replace(/\s+/g, '-') === slug ||
            p.id === slug
          );
          if (found) {
            setPackageData(found);
          } else {
            setError('Package not found');
          }
        }
        
        // Always fetch all packages for related section
        const allRes = await publicAPI.getPackages();
        setAllPackages(allRes.data);
      } catch (err) {
        console.error('Error fetching package:', err);
        setError('Failed to load package details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-32 pb-16 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#007897]"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !packageData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="pt-32 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Package Not Found</h1>
            <p className="text-gray-600 mb-8">The package you are looking for does not exist or has been removed.</p>
            <Link
              to="/packages"
              className="inline-flex items-center gap-2 bg-[#007897] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#006680] transition-colors"
            >
              <ArrowLeft size={18} /> Back to Packages
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const discount = packageData.original_price > packageData.price 
    ? Math.round(((packageData.original_price - packageData.price) / packageData.original_price) * 100) 
    : 0;

  const relatedPackages = allPackages
    .filter(p => p.id !== packageData.id && p.category === packageData.category)
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-[#007897] to-[#005a73]">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-white/70 mb-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <Link to="/packages" className="hover:text-white">Health Packages</Link>
            <span>/</span>
            <span className="text-white">{packageData.name}</span>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <span className="inline-block bg-white/20 px-4 py-1 rounded-full text-sm font-medium mb-4">
                {packageData.category}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{packageData.name}</h1>
              <p className="text-white/80 text-lg mb-6">
                {packageData.description || `Comprehensive health screening package with ${packageData.tests.length} essential tests for complete health assessment.`}
              </p>
              
              <div className="flex items-center gap-4 mb-8">
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold">₹{packageData.price.toLocaleString()}</span>
                  {packageData.original_price > packageData.price && (
                    <span className="text-xl text-white/60 line-through">₹{packageData.original_price.toLocaleString()}</span>
                  )}
                </div>
                {discount > 0 && (
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                    {discount}% OFF
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-4">
                <a href="/contact">
                  <Button size="lg" className="bg-white text-[#007897] hover:bg-gray-100 px-8">
                    <Phone size={18} className="mr-2" />
                    Contact Us
                  </Button>
                </a>
              </div>
            </div>

            {/* Quick Info Card */}
            <div className="bg-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Package Highlights</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-[#007897]/10 rounded-xl flex items-center justify-center">
                    <Beaker className="w-6 h-6 text-[#007897]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Total Tests</p>
                    <p className="text-xl font-bold text-gray-900">{packageData.tests.length} Tests</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-[#007897]/10 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-[#007897]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Report Time</p>
                    <p className="text-xl font-bold text-gray-900">{packageData.reporting_time || packageData.report_time || 'Same Day'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
                  <div className="w-12 h-12 bg-[#007897]/10 rounded-xl flex items-center justify-center">
                    <IndianRupee className="w-6 h-6 text-[#007897]" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">You Save</p>
                    <p className="text-xl font-bold text-green-600">₹{(packageData.original_price - packageData.price).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tests Included */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Tests Included</h2>
            <p className="text-gray-600">This package includes the following {packageData.tests.length} tests</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {packageData.tests.map((test, index) => (
              <div
                key={`item-${index}`}
                className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-[#007897]/5 transition-colors"
              >
                <div className="w-8 h-8 bg-[#007897] rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <span className="text-gray-700 font-medium">{test}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Should Take This Package */}
      {packageData.who_should_take && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="bg-[#007897]/5 rounded-2xl p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#007897]/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6 text-[#007897]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Who Should Take This Package?</h3>
                    <p className="text-gray-700 leading-relaxed">{packageData.who_should_take}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Preparations */}
      {packageData.preparations && packageData.preparations.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="bg-amber-50 rounded-2xl p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Test Preparations</h3>
                    <ul className="space-y-3">
                      {packageData.preparations.map((prep, index) => (
                        <li key={`item-${index}`} className="flex items-start gap-3 text-gray-700">
                          <span className="w-6 h-6 bg-amber-200 rounded-full flex items-center justify-center text-sm font-medium text-amber-800 flex-shrink-0">
                            {index + 1}
                          </span>
                          {prep}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#007897] to-[#005a73]">
        <div className="max-w-4xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Book Your {packageData.name} Today</h2>
          <p className="text-white/80 mb-8">
            Get accurate and timely results with our state-of-the-art diagnostic facilities.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/contact">
              <Button size="lg" className="bg-white text-[#007897] hover:bg-gray-100 px-8">
                <Phone size={18} className="mr-2" />
                Contact Us
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Related Packages */}
      {relatedPackages.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Related Packages</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedPackages.map((pkg) => (
                <Link
                  key={pkg.id}
                  to={`/packages/${pkg.slug || pkg.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all p-6 group"
                >
                  <span className="text-xs bg-[#007897]/10 text-[#007897] px-2 py-1 rounded-full">
                    {pkg.category}
                  </span>
                  <h3 className="font-semibold text-gray-900 mt-3 group-hover:text-[#007897] transition-colors">
                    {pkg.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-2">{pkg.tests.length} Tests</p>
                  <div className="flex items-baseline gap-2 mt-3">
                    <span className="text-xl font-bold text-[#007897]">₹{pkg.price.toLocaleString()}</span>
                    {pkg.original_price > pkg.price && (
                      <span className="text-sm text-gray-400 line-through">₹{pkg.original_price.toLocaleString()}</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default PackageDetailPage;
